import React, {useEffect, useRef, useState} from 'react';
import {Box, IconButton, MenuItem, Select, Tooltip, Typography} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
// import * as http from '../util/elemental_axios_client';

import {MaterialReactTable, useMaterialReactTable} from 'material-react-table';
import { useAuth } from 'contexts/AuthContext';

const defaultRowsPerPage = [5, 10, 15, 20, 25, 30, 50, 100];

// ref: https://github.com/KevinVandy/material-react-table/blob/v2/packages/material-react-table/src/toolbar/MRT_TablePagination.tsx
export const MyTablePagination = ({
    position = 'bottom',
    table,
    ...rest
}) => {
    // options set from the table
    const {
        getState,
        options: {
            enableToolbarInternalActions,
            icons: { ChevronLeftIcon, ChevronRightIcon },
            localization
        },
        setPageIndex,
        setPageSize
    } = table;
    // from the state of the table
    const {
        pagination: { pageIndex = 0, pageSize = 10 },
        showGlobalFilter
    } = getState();

    const paginationProps = {
        ...rest
    };

    const {
        SelectProps,
        rowsPerPageOptions = defaultRowsPerPage,
        showRowsPerPage = true,
        hasPreviousPage,
        hasNextPage,
        ..._rest
    } = paginationProps ?? {};

    return (
        <Box
            className='MuiTablePagination-root'
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: { md: 'space-between', sm: 'center' },
                justifySelf: 'flex-end',
                mt:
                    position === 'top' &&
                    enableToolbarInternalActions &&
                    !showGlobalFilter
                        ? '3rem'
                        : undefined,
                position: 'relative',
                px: '8px',
                py: '12px',
                zIndex: 2
            }}
        >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
                {/*<InputLabel htmlFor='mrt-rows-per-page' sx={{ mb: 0 }}>*/}
                {/*    {localization.rowsPerPage}*/}
                {/*</InputLabel>*/}
                <Select
                    disableUnderline
                    id='mrt-rows-per-page'
                    inputProps={{ 'aria-label': localization.rowsPerPage }}
                    label={localization.rowsPerPage}
                    onChange={(event) => setPageSize(+(event.target.value))}
                    sx={{ mb: 0 }}
                    value={pageSize}
                    variant='standard'
                    {...SelectProps}
                >
                    {rowsPerPageOptions.map((option) => {
                        const value = typeof option !== 'number' ? option.value : option;
                        const label =
                            typeof option !== 'number' ? option.label : `${option}`;
                        return (
                            SelectProps?.children ??
                            (SelectProps?.native ? (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ) : (
                                <MenuItem key={value} sx={{ m: 0 }} value={value}>
                                    {label}
                                </MenuItem>
                            ))
                        );
                    })}
                </Select>
            </Box>
            <Box gap='xs'>
                <IconButton
                    aria-label={localization.goToPreviousPage}
                    disabled={!hasPreviousPage}
                    onClick={() => setPageIndex(pageIndex - 1)}
                    size='small'
                >
                    <ChevronLeftIcon />
                </IconButton>
                <IconButton
                    aria-label={localization.goToNextPage}
                    disabled={!hasNextPage}
                    onClick={() => setPageIndex(pageIndex + 1)}
                    size='small'
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>
        </Box>
    );
};








const MaterialReactTablePagedKeyset = ({
    columns,

    queryBaseKey,
    queryBaseUrl = '',
    // will switch to post and pass this as a payload
    queryPayload = null,

    queryPageDataKey = 'page',

    collectionName='Things',
    onRowClick = null,
    // reactQuery options passed to useQuery
    queryOptions = {},

    // New sorting props
    sortOptions = [], // Array of { value: 'field.direction', label: 'Display Name' }
    defaultSort = '', // Default sort value from sortOptions

    loadingComponent = null,
    emptyStateComponent = null,

    axiosInstance = null,

        // passthru options
    ...materialReactTableProps

} ) => {
    /*
     * Sorting Behavior:
     * - Custom sort dropdown above the table
     * - Sort options are defined via sortOptions prop
     * - Default sort can be set via defaultSort prop
     * - Sorting is handled via URL parameters in the API call
     */

    // Custom sort state
    const [selectedSort, setSelectedSort] = useState(defaultSort);

    const { axiosElementalApiCurrentOrg } = useAuth();

    let lastPageIndexRef = useRef(0);

    const [dataPage, setDataPage] = useState([]);

    // stores the slug of the current fetched page. putting a slug here will trigger a reload of that page
    const [loadPageSlug, setLoadPageSlug] = useState('');
    const [currentPageSlug, setCurrentPageSlug] = useState('');

    const [hasNextPage, setHasNextPage] = useState(false);
    const [nextPageSlug, setNextPageSlug] = useState('');

    const [hasPreviousPage, setHasPreviousPage] = useState(false);
    const [previousPageSlug, setPreviousPageSlug] = useState('');

    // START Material React Table
    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 100
    });

    // get next/prev PageSlug when pageIndex changes
    useEffect(() => {
        console.log('pagination', pagination);
        //do something when the pagination state changes
        if ( pagination.pageIndex == 0 ) {
            // reset the slugs
            setLoadPageSlug('');
        }
        else if (pagination.pageIndex > lastPageIndexRef.current) {
            // increase page
            if (hasNextPage) {
                setLoadPageSlug(nextPageSlug);
            }
        }
        else {
            // decrease page
            if (hasPreviousPage) {
                setLoadPageSlug(previousPageSlug);
            }
        }
        lastPageIndexRef.current = pagination.pageIndex;

        // page size should reset the slug to empty?
    }, [pagination.pageIndex, pagination.pageSize]);

    if ( !axiosInstance ) {
        console.log('no axios instance provided, using axiosElementalApiCurrentOrg');
        axiosInstance = axiosElementalApiCurrentOrg;
    }
    else{ 
        console.log('axios instance provided, using axiosInstance with base URL:', axiosInstance.defaults.baseURL);
    }

    const { data, isError, isFetching, isLoading, refetch } = useQuery({
        queryKey: [
            ...queryBaseKey,
            queryPayload,
            pagination.pageSize,
            loadPageSlug,
            selectedSort, // Include selectedSort in queryKey
            // columnFilters, //refetch when columnFilters changes
            // globalFilter, //refetch when globalFilter changes
            // pagination.pageIndex, //refetch when pagination.pageIndex changes
            // pagination.pageSize, //refetch when pagination.pageSize changes
            // sorting //refetch when sorting changes
        ],
        queryFn: async () => {
            console.log('selectedSort', selectedSort);
            // need to parse queryBaseUrl to see if it has url_args and then append these args
            let url = `${queryBaseUrl}`;
            if (!queryBaseUrl.includes('?')) {
                url = `${url}?`;
            }
            else {
                url = `${url}&`;
            }
            
            // Add sort parameter if selectedSort is set
            let sortParam = '';
            if (selectedSort) {
                sortParam = `&sort=${selectedSort}`;
            }
            
            url = `${url}per_page=${pagination.pageSize}&page=${loadPageSlug}${sortParam}`;
            if (queryPayload){
                return await axiosInstance.post(url, queryPayload);
            }
            else{
                return await axiosInstance.get(url);
            }
        },
        // onSuccess: (data) => {
        //     setStateFromDataAndPaging(data);
        // },
        onError: (error) => {
            console.log('error', error);
        },
        ...queryOptions,
        keepPreviousData: true
    });

    // const setStateFromDataAndPaging = async ( data_and_paging ) => {
    //     setDataPage(data_and_paging[queryPageDataKey]);
    //     setCurrentPageSlug(data_and_paging['paging']['current']);
    //     setNextPageSlug(data_and_paging['paging']['next']);
    //     setHasNextPage(data_and_paging['paging']['has_next']);
    //     setPreviousPageSlug(data_and_paging['paging']['previous']);
    //     setHasPreviousPage(data_and_paging['paging']['has_previous']);
    // };

    useEffect(() => {
        if(data){
            setDataPage(data[queryPageDataKey]);
            setCurrentPageSlug(data['paging']['current']);
            setNextPageSlug(data['paging']['next']);
            setHasNextPage(data['paging']['has_next']);
            setPreviousPageSlug(data['paging']['previous']);
            setHasPreviousPage(data['paging']['has_previous']);
        }
    }, [data, queryPageDataKey ]);


    const MRT_defaults = {
        // defaults here ..
        // turn off filtering for now
        enableFilters: false,
        manualFiltering: true,

        // Disable Material React Table sorting since we're using custom sort dropdown
        enableSorting: false,
        manualSorting: true,

        enableDensityToggle:false,
        enableFullScreenToggle:false,

        positionActionsColumn:'last',

        enableHiding: false,
        enableColumnActions: false,
        enableTopToolbar: false,

        // override with user provided values
        ...materialReactTableProps
    };

    // END Material React Table
    const table = useMaterialReactTable({
        columns: columns, // Use the original columns array
        data: dataPage, //data is undefined on first render
        manualSorting: true,
        muiToolbarAlertBannerProps:
            isError ? {
                color: 'error',
                children: 'Error loading data'
            } : undefined,

        // pagination
        manualPagination:true,
        pageCount : -1,
        rowCount: pagination.pageSize * pagination.pageIndex + 1,
        hideNextButton: !hasNextPage,
        hidePrevButton: !hasPreviousPage,

        // use the MUI componenet so its props can get set

        // paginationDisplayMode: 'pages',
        // https://mui.com/material-ui/api/pagination/
        // https://mui.com/material-ui/react-pagination/

        // rowsPerPage: '',

        // https://github.com/KevinVandy/material-react-table/blob/v2/packages/material-react-table/src/toolbar/MRT_TablePagination.tsx
        // muiPaginationProps:{
        //     // count: -1,
        //     labelDisplayedRows: ({from, to, count}) => { return 'Hello world'; },
        //     backIconButtonProps: { disabled: !hasPreviousPage },
        //     nextIconButtonProps: { disabled: !hasNextPage },
        //     hideNextButton: !hasNextPage,
        //     hidePrevButton: !hasPreviousPage,
        //
        //
        //     showFirstButton: false,
        //     showLastButton: false
        // },


        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,

        state: {
            columnFilters,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isFetching,
            density: 'compact'
        },
        muiTableBodyRowProps: ({ row }) => ({
            onClick: (event) => {
                if(onRowClick){
                    onRowClick(event,row);
                }
            },
            sx: {
                cursor: 'pointer' //you might want to change the cursor too when adding an onClick
            }
        }),

        // renderBottomToolbarCustomActions: ({ table }) => (
        renderBottomToolbar: ({ table }) => (
            <Box
                sx={{
                    // display: 'flex',
                    // // justifyContent: 'space-between',
                    // alignItems: 'end'

                    display: 'flex',
                    justifyContent: 'flex-end',
                    // position: stackAlertBanner ? 'relative' : 'absolute',
                    right: 0,
                    top: 0

                }}
            >
                <MyTablePagination table={table} hasNextPage={hasNextPage} hasPreviousPage={hasPreviousPage}  />
            </Box>
        ),

        ...MRT_defaults

    });

    // START Existing custom code




    // TODO: loader should just be in the table ? put here will make the whole screen blink
    // if ( getPageQuery.isLoading) {
    //     return <Loader />;
    // }
    // if ( !getPageQuery.isSuccess ){
    //     return <div>Something went wrong</div>;
    // }
    // TODO: incorporate empty state directly into the KeysetPagingTable component
    // return <EmptyState image={<img src={audiencesEmpty} alt='empty_graphics'/>} title={'No Audiences'} text={'You don\'t have any audience at the moment'} />;

    const defaultLoadingState = (
        <Box sx={{p: 3, textAlign: 'center'}}>
            <Typography variant='h6'>Loading {collectionName}...</Typography>
        </Box>
    );

    const defaultEmptyState = (
        <Box sx={{p: 3, textAlign: 'center'}}>
            <Typography variant='h6'>No {collectionName}</Typography>
            {/*<Typography variant='body2'>Please add some data to see the table content.</Typography>*/}
        </Box>
    );

    return (
        <>
            {isLoading && (
                loadingComponent || defaultLoadingState
            )}
            {!isLoading && (!data || data?.[queryPageDataKey]?.length === 0) && (
                emptyStateComponent || defaultEmptyState
            )}
            {!isLoading && data && data?.[queryPageDataKey]?.length > 0 && (
                <>
                    {/* Custom Sort Dropdown */}
                    {sortOptions.length > 0 && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            mb: 2,
                            px: 2
                        }}>
                            <Select
                                value={selectedSort}
                                onChange={(e) => setSelectedSort(e.target.value)}
                                displayEmpty
                                size="small"
                                sx={{ minWidth: 200 }}
                            >
                                <MenuItem value="">
                                    <em>Sort: Sort by...</em>
                                </MenuItem>
                                {sortOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        Sort: {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}
                    
                    <MaterialReactTable
                        table={table}
                        // isLoading={isLoading}
                    />
                </>
            )}
        </>
    );
};


export default MaterialReactTablePagedKeyset;